import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';

import { Constants } from '../../common';
import { DynamicHeight } from '../../helper';
import { ICampaignBusinessScreensResponse, IScreenListItem, IScreenStatCard } from '../../models';
import { CampaignService, CommonService } from '../../services';

@Component({
  selector: 'app-screens',
  imports: [CommonModule, MatTableModule, MatSortModule, MatIconModule, DynamicHeight],
  templateUrl: './screens.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './screens.component.scss',
})
export class ScreensComponent implements OnInit {
  private readonly screensViewModeStorageKey = Constants.SCREENS_VIEW_MODE_KEY;
  private readonly collapsedGroupKeys = new Set<string>();

  readonly displayedColumns: string[] = [
    'serial_no',
    'screen_name',
    'business_name',
    'operational_status',
    'filler_ad',
    'action_button'
  ];
  readonly groupedDisplayedColumns: string[] = [
    'serial_no',
    'screen_name',
    'operational_status',
    'filler_ad',
    'action_button'
  ];

  readonly dataSource = new MatTableDataSource<IScreenListItem>([]);

  statCards: IScreenStatCard[] = [];
  allScreens: IScreenListItem[] = [];
  groupedScreens: Array<{
    businessId: number | null;
    businessName: string;
    screens: IScreenListItem[];
  }> = [];
  viewMode: 'list' | 'grouped' = 'list';

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public commonService: CommonService,
    private campaignService: CampaignService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.viewMode = this.getStoredViewMode();
    this.initializeFilter();
    this.loadScreens();
  }

  initializeFilter() {
    this.dataSource.filterPredicate = (data: IScreenListItem, filter: string) => {
      const searchValue = filter.trim().toLowerCase();
      return [
        data.screenName,
        data.businessName,
        data.operationalStatus,
        data.activeState,
        data.fillerAdEnabled ? 'enabled' : 'disabled',
        data.screenId.toString()
      ].some(value => value.toLowerCase().includes(searchValue));
    };
  }

  loadScreens() {
    this.commonService.showSpinner = true;

    this.campaignService.getAllBusinessScreens().subscribe({
      next: (businessScreens) => {
        const screenList = this.buildScreenList(businessScreens ?? []);
        this.allScreens = screenList;
        this.dataSource.data = screenList;
        this.dataSource.sort = this.sort;
        this.statCards = this.buildStatCards(screenList);
        this.groupedScreens = this.buildGroupedScreens(screenList);
        this.commonService.showSpinner = false;
      },
      error: () => {
        this.allScreens = [];
        this.dataSource.data = [];
        this.statCards = this.buildStatCards([]);
        this.groupedScreens = [];
        this.commonService.showSpinner = false;
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    this.groupedScreens = this.buildGroupedScreens(this.dataSource.filteredData);
  }

  setViewMode(mode: 'list' | 'grouped') {
    this.viewMode = mode;
    localStorage.setItem(this.screensViewModeStorageKey, mode);
  }

  toggleGroup(businessId: number | null, businessName: string) {
    const groupKey = this.getGroupKey(businessId, businessName);

    if (this.collapsedGroupKeys.has(groupKey)) {
      this.collapsedGroupKeys.delete(groupKey);
      return;
    }

    this.collapsedGroupKeys.add(groupKey);
  }

  isGroupExpanded(businessId: number | null, businessName: string): boolean {
    return !this.collapsedGroupKeys.has(this.getGroupKey(businessId, businessName));
  }

  openScreenDetail(screen: IScreenListItem) {
    this.router.navigate([Constants.APP_ROUTES.screenDetail(screen.screenId)]);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'live':
        return 'status-live';
      case 'standby':
        return 'status-standby';
      case 'active':
        return 'status-active';
      case 'inactive':
      default:
        return 'status-inactive';
    }
  }

  private buildScreenList(businessScreens: ICampaignBusinessScreensResponse[]): IScreenListItem[] {
    const screenList: IScreenListItem[] = [];
    businessScreens.forEach((business) => {
      business.screens.forEach((screen) => {
        screenList.push({
          screenId: screen.screen_id,
          screenName: this.asText(screen.screen_name, `Screen ${screen.screen_id}`),
          businessId: this.asNullableNumber(business.business_id),
          businessName: this.asText(business.business_name, 'Unassigned'),
          operationalStatus: this.resolveOperationalStatus(screen.status),
          activeState: screen.is_active ? 'ACTIVE' : 'INACTIVE',
          fillerAdEnabled: Boolean(screen.is_admin_ad_enabled)
        });
      });
    });
    return screenList;
  }

  private buildStatCards(screenList: IScreenListItem[]): IScreenStatCard[] {
    const totalBusinesses = new Set(
      screenList.map(screen => this.getGroupKey(screen.businessId, screen.businessName))
    ).size;

    const counts = {
      total: screenList.length,
      businesses: totalBusinesses,
      fillerAdEnabled: screenList.filter(screen => screen.fillerAdEnabled).length,
      live: screenList.filter(screen => screen.operationalStatus.toLowerCase() === 'live').length,
      standby: screenList.filter(screen => screen.operationalStatus.toLowerCase() === 'standby').length,
      inactive: screenList.filter(screen => screen.operationalStatus.toLowerCase() === 'inactive').length,
    };

    return [
      { label: 'Total Screens', value: counts.total, className: 'card-total' },
      { label: 'Total Business', value: counts.businesses, className: 'card-business' },
      { label: 'Filler Ad Screens', value: counts.fillerAdEnabled, className: 'card-filler-ad' },
      { label: 'Live', value: counts.live, className: 'card-live' },
      { label: 'Standby', value: counts.standby, className: 'card-standby' },
      { label: 'Inactive', value: counts.inactive, className: 'card-inactive' },
    ];
  }

  private buildGroupedScreens(screenList: IScreenListItem[]) {
    const groupedMap = new Map<string, { businessId: number | null; businessName: string; screens: IScreenListItem[] }>();

    screenList.forEach((screen) => {
      const key = this.getGroupKey(screen.businessId, screen.businessName);
      const existingGroup = groupedMap.get(key);

      if (existingGroup) {
        existingGroup.screens.push(screen);
        return;
      }

      groupedMap.set(key, {
        businessId: screen.businessId,
        businessName: screen.businessName,
        screens: [screen]
      });
    });

    return Array.from(groupedMap.values()).sort((left, right) =>
      left.businessName.localeCompare(right.businessName)
    );
  }

  private resolveOperationalStatus(status: string | null | undefined): string {
    const rawStatus = this.asText(status, '');
    const normalizedStatus = rawStatus.toLowerCase();

    if (['live', 'standby', 'inactive', 'active'].includes(normalizedStatus)) {
      return rawStatus.toUpperCase();
    }

    return 'INACTIVE';
  }

  private asNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private asText(value: unknown, fallback: string): string {
    return this.asNullableText(value) ?? fallback;
  }

  private asNullableText(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    return null;
  }

  private getStoredViewMode(): 'list' | 'grouped' {
    const storedViewMode = localStorage.getItem(this.screensViewModeStorageKey);
    return storedViewMode === 'grouped' ? 'grouped' : 'list';
  }

  private getGroupKey(businessId: number | null, businessName: string): string {
    return `${businessId ?? 'none'}-${businessName}`;
  }
}
